import { useEffect, useRef, useState } from "react";
import { CircleMarker, Popup } from "react-leaflet";
import { toast } from "sonner";
import { Camera, Trash2, StickyNote } from "lucide-react";
import { api, API, errorMessage, formatDate } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const token = () => localStorage.getItem("cgland_token");

export function NoteMarkers({ notes, onDelete }) {
  return notes.map((n) => (
    <CircleMarker key={n.id} center={[n.lat, n.lng]} radius={8} pathOptions={{ color: "#fff", weight: 2, fillColor: n.color || "#C85A32", fillOpacity: 1 }} pane="markerPane">
      <Popup maxWidth={280}>
        <div data-testid={`note-popup-${n.id}`} className="space-y-2 text-sm min-w-[200px]">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1"><StickyNote size={11} /> Field note · {formatDate(n.created_at)}</p>
          {n.user_email && <p className="text-xs font-semibold">{n.user_name || n.user_email} <span className="text-slate-500 font-normal">({n.user_email})</span></p>}
          {n.has_photo && <img src={`${API}/map/notes/${n.id}/photo?auth=${token()}`} alt="Field photo" className="rounded-lg w-full max-h-40 object-cover" />}
          {n.text && <p className="whitespace-pre-wrap">{n.text}</p>}
          <p className="font-mono text-[11px] text-slate-500">{n.lat.toFixed(5)}, {n.lng.toFixed(5)}{n.accuracy ? ` · ±${Math.round(n.accuracy)} m` : ""}</p>
          <button type="button" data-testid="note-delete-button" onClick={() => onDelete(n)} className="text-xs text-red-600 flex items-center gap-1 hover:underline"><Trash2 size={12} /> Delete note</button>
        </div>
      </Popup>
    </CircleMarker>
  ));
}

export function NoteDialog({ position, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (!position) { setText(""); setPhoto(null); setPreview(null); } }, [position]);
  const pick = (f) => { setPhoto(f || null); setPreview(f ? URL.createObjectURL(f) : null); };

  const save = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("lat", position.lat); fd.append("lng", position.lng); fd.append("text", text);
    if (position.accuracy) fd.append("accuracy", position.accuracy);
    if (photo) fd.append("photo", photo);
    setBusy(true);
    try { const { data } = await api.post("/map/notes", fd, { headers: { "Content-Type": "multipart/form-data" } }); onSaved(data); toast.success("Field note saved"); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!position} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="note-dialog" className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><StickyNote size={18} style={{ color: "var(--terracotta)" }} /> New field note</DialogTitle>
          <DialogDescription className="font-mono text-xs">{position ? `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}${position.accuracy ? ` · GPS ±${Math.round(position.accuracy)} m` : " · pinned on map"}` : ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Textarea data-testid="note-text-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="What did you observe? Boundary stone, encroachment, crop type…" rows={4} className="rounded-xl" />
          <div>
            <input ref={fileRef} data-testid="note-photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
            {preview ? (
              <div className="relative"><img src={preview} alt="Preview" className="rounded-xl max-h-48 w-full object-cover" /><button type="button" onClick={() => pick(null)} className="absolute top-2 right-2 text-xs bg-white/90 rounded-full px-2 py-0.5">Remove</button></div>
            ) : (
              <button type="button" data-testid="note-photo-button" onClick={() => fileRef.current?.click()} className="w-full rounded-xl border-2 border-dashed p-5 text-sm text-muted-foreground flex items-center justify-center gap-2 hover:border-emerald-400"><Camera size={16} /> Add photo (camera or gallery)</button>
            )}
          </div>
          <Button data-testid="note-save-button" type="submit" disabled={busy || (!text.trim() && !photo)} className="w-full h-11 rounded-full text-white hover:opacity-90" style={{ background: "var(--terracotta)" }}>{busy ? "Saving…" : "Save note"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
